import React, { useState, useCallback } from 'react';
import './Evaluate.css';

function ScoreBar({ label, value, max = 5 }) {
  const pct = (value / max) * 100;
  const color = value >= 4 ? '#22c55e' : value >= 3 ? '#eab308' : '#ef4444';
  return (
    <div className="score-bar-row">
      <span className="score-bar-label">{label}</span>
      <div className="score-bar-track">
        <div className="score-bar-fill" style={{ width: `${pct}%`, background: color }} />
      </div>
      <span className="score-bar-value">{value.toFixed(1)}</span>
    </div>
  );
}

function QualityScores({ scores }) {
  if (!scores) return null;
  if (scores.error) return <div className="eval-quality-error">Judge error: {scores.error}</div>;
  const safe = (v) => typeof v === 'number' ? v : null;
  return (
    <div className="eval-quality">
      <div className="eval-quality-title">Quality ({scores.source === 'promptfoo' ? 'promptfoo' : 'LLM Judge'})</div>
      {safe(scores.accuracy) != null && <ScoreBar label="Accuracy" value={scores.accuracy} />}
      {safe(scores.relevance) != null && <ScoreBar label="Relevance" value={scores.relevance} />}
      {safe(scores.coherence) != null && <ScoreBar label="Coherence" value={scores.coherence} />}
      {safe(scores.completeness) != null && <ScoreBar label="Completeness" value={scores.completeness} />}
      {safe(scores.conciseness) != null && <ScoreBar label="Conciseness" value={scores.conciseness} />}
      {safe(scores.overall) != null && (
        <div className="eval-quality-overall">
          <span>Overall</span><span className="eval-quality-overall-value">{scores.overall.toFixed(1)}/5</span>
        </div>
      )}
      {scores.pass != null && (
        <div className={`eval-quality-pass ${scores.pass ? 'pass' : 'fail'}`}>{scores.pass ? '✓ Pass' : '✗ Fail'}</div>
      )}
      {scores.explanation && <div className="eval-quality-explanation">{scores.explanation}</div>}
    </div>
  );
}

function MetricsSummary({ metrics }) {
  if (!metrics) return null;
  const { latency, tokens, cost } = metrics;
  const fmtMs = (ms) => ms != null ? `${ms.toLocaleString()}ms` : '—';
  const fmtCost = (v) => v != null ? `$${v.toFixed(6)}` : '—';
  return (
    <div className="eval-perf">
      <div className="eval-perf-title">Performance</div>
      <div className="eval-perf-grid">
        <div className="eval-perf-item"><span className="eval-perf-label">Latency</span><span className="eval-perf-value">{fmtMs(latency.totalResponseTimeMs)}</span></div>
        <div className="eval-perf-item"><span className="eval-perf-label">Bedrock</span><span className="eval-perf-value">{fmtMs(latency.bedrockLatencyMs)}</span></div>
        <div className="eval-perf-item"><span className="eval-perf-label">Input tokens</span><span className="eval-perf-value">{tokens.inputTokens.toLocaleString()}</span></div>
        <div className="eval-perf-item"><span className="eval-perf-label">Output tokens</span><span className="eval-perf-value">{tokens.outputTokens.toLocaleString()}</span></div>
        <div className="eval-perf-item"><span className="eval-perf-label">Total tokens</span><span className="eval-perf-value">{tokens.totalTokens.toLocaleString()}</span></div>
        <div className="eval-perf-item"><span className="eval-perf-label">Est. cost</span><span className="eval-perf-value">{fmtCost(cost.totalCost)}</span></div>
      </div>
    </div>
  );
}

// --- Report Generation ---

function generateReport(allResults, modelNames, selectedModels) {
  const modelIds = selectedModels;
  const n = (id) => modelNames[id] || id;
  const now = new Date().toISOString().split('T')[0];

  // Aggregate stats per model across all prompts
  const stats = {};
  modelIds.forEach(id => { stats[id] = { latencies: [], costs: [], tokens: [], scores: [], prompts: 0 }; });

  allResults.forEach(r => {
    if (!r.evaluations) return;
    r.evaluations.forEach(ev => {
      const s = stats[ev.modelId];
      if (!s) return;
      s.prompts++;
      if (ev.metrics) {
        s.latencies.push(ev.metrics.latency.totalResponseTimeMs || 0);
        s.costs.push(ev.metrics.cost.totalCost || 0);
        s.tokens.push(ev.metrics.tokens.totalTokens || 0);
      }
      if (ev.qualityScores?.overall) s.scores.push(ev.qualityScores.overall);
    });
  });

  const avg = (arr) => arr.length ? (arr.reduce((a, b) => a + b, 0) / arr.length) : 0;
  const sum = (arr) => arr.reduce((a, b) => a + b, 0);

  let md = `# Model Evaluation Report\n\n`;
  md += `**Date:** ${now}  \n`;
  md += `**Models Evaluated:** ${modelIds.map(n).join(', ')}  \n`;
  md += `**Prompts Tested:** ${allResults.length}  \n`;
  md += `**Judge:** promptfoo (LLM-as-Judge via Bedrock)\n\n`;
  md += `---\n\n`;

  // Performance table
  md += `## Performance Characteristics\n\n`;
  md += `| Metric | ${modelIds.map(n).join(' | ')} |\n`;
  md += `| --- | ${modelIds.map(() => '---').join(' | ')} |\n`;
  md += `| Avg Latency (ms) | ${modelIds.map(id => avg(stats[id].latencies).toFixed(0)).join(' | ')} |\n`;
  md += `| Avg Tokens | ${modelIds.map(id => avg(stats[id].tokens).toFixed(0)).join(' | ')} |\n`;
  md += `| Total Cost | ${modelIds.map(id => '$' + sum(stats[id].costs).toFixed(6)).join(' | ')} |\n`;
  md += `| Avg Cost/Prompt | ${modelIds.map(id => '$' + avg(stats[id].costs).toFixed(6)).join(' | ')} |\n`;
  md += `| Avg Quality (1-5) | ${modelIds.map(id => stats[id].scores.length ? avg(stats[id].scores).toFixed(1) : 'N/A').join(' | ')} |\n\n`;

  // Quality breakdown
  md += `## Quality Scores by Prompt\n\n`;
  allResults.forEach((r, pi) => {
    md += `### Prompt ${pi + 1}: "${r.prompt}"\n\n`;
    md += `| Model | Overall | Accuracy | Relevance | Coherence |\n`;
    md += `| --- | --- | --- | --- | --- |\n`;
    (r.evaluations || []).forEach(ev => {
      const qs = ev.qualityScores || {};
      const f = (v) => typeof v === 'number' ? v.toFixed(1) : '—';
      md += `| ${n(ev.modelId)} | ${f(qs.overall)} | ${f(qs.accuracy)} | ${f(qs.relevance)} | ${f(qs.coherence)} |\n`;
    });
    md += `\n`;
  });

  // Use case recommendations
  md += `## Use Case Recommendations\n\n`;
  const fastest = modelIds.reduce((a, b) => avg(stats[a].latencies) < avg(stats[b].latencies) ? a : b);
  const cheapest = modelIds.reduce((a, b) => sum(stats[a].costs) < sum(stats[b].costs) ? a : b);
  const bestQuality = modelIds.filter(id => stats[id].scores.length > 0);
  const highest = bestQuality.length ? bestQuality.reduce((a, b) => avg(stats[a].scores) > avg(stats[b].scores) ? a : b) : null;

  md += `- **Lowest Latency:** ${n(fastest)} (avg ${avg(stats[fastest].latencies).toFixed(0)}ms) — best for real-time/interactive use cases\n`;
  md += `- **Most Cost-Effective:** ${n(cheapest)} (total $${sum(stats[cheapest].costs).toFixed(6)}) — best for high-volume batch processing\n`;
  if (highest) md += `- **Highest Quality:** ${n(highest)} (avg ${avg(stats[highest].scores).toFixed(1)}/5) — best for accuracy-critical applications\n`;
  md += `\n`;

  // Trade-offs
  md += `## Trade-offs Observed\n\n`;
  modelIds.forEach(id => {
    const s = stats[id];
    const avgLat = avg(s.latencies).toFixed(0);
    const avgScore = s.scores.length ? avg(s.scores).toFixed(1) : 'N/A';
    const totalCost = sum(s.costs).toFixed(6);
    md += `**${n(id)}:** Avg latency ${avgLat}ms, quality ${avgScore}/5, total cost $${totalCost}. `;
    if (id === fastest && id !== highest) md += `Fast but may sacrifice quality. `;
    if (id === highest && id !== fastest) md += `High quality but slower. `;
    if (id === cheapest && id !== highest) md += `Cost-effective but may not be the most accurate. `;
    if (id === fastest && id === highest) md += `Best balance of speed and quality. `;
    md += `\n`;
  });

  return md;
}

function downloadMarkdown(content, filename) {
  const blob = new Blob([content], { type: 'text/markdown' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

// --- Main Component ---

function Evaluate({ idToken, config, models, onBack }) {
  const [prompts, setPrompts] = useState(['']);
  const [selectedModels, setSelectedModels] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingStatus, setLoadingStatus] = useState('');
  const [allResults, setAllResults] = useState(null);
  const [activePromptIdx, setActivePromptIdx] = useState(null);

  const toggleModel = (modelId) => {
    setSelectedModels((prev) => {
      if (prev.includes(modelId)) return prev.filter((id) => id !== modelId);
      if (prev.length >= 3) return prev;
      return [...prev, modelId];
    });
  };

  const updatePrompt = (idx, value) => {
    setPrompts(prev => { const next = [...prev]; next[idx] = value; return next; });
  };

  const addPrompt = () => { if (prompts.length < 10) setPrompts(prev => [...prev, '']); };
  const removePrompt = (idx) => { if (prompts.length > 1) setPrompts(prev => prev.filter((_, i) => i !== idx)); };

  const pollForResults = useCallback(async (jobId) => {
    for (let i = 0; i < 90; i++) {
      await new Promise(r => setTimeout(r, 2000));
      try {
        const res = await fetch(`${config.apiUrl}evaluate?jobId=${jobId}`, { headers: { 'Authorization': idToken } });
        if (!res.ok) continue;
        const data = await res.json();
        if (data.status === 'scoring') setLoadingStatus(s => s); // keep current
        if (data.status === 'complete') return data;
        if (data.status === 'error') throw new Error(data.error || 'Evaluation failed');
      } catch (err) { if (i === 89) throw err; }
    }
    throw new Error('Evaluation timed out');
  }, [config, idToken]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validPrompts = prompts.filter(p => p.trim());
    if (validPrompts.length === 0 || selectedModels.length === 0) return;

    setIsLoading(true);
    setAllResults(null);
    setActivePromptIdx(null);
    const results = [];

    try {
      for (let i = 0; i < validPrompts.length; i++) {
        setLoadingStatus(`Prompt ${i + 1}/${validPrompts.length}: Starting...`);
        const response = await fetch(`${config.apiUrl}evaluate`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': idToken },
          body: JSON.stringify({ prompt: validPrompts[i], modelIds: selectedModels }),
        });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const { jobId } = await response.json();
        setLoadingStatus(`Prompt ${i + 1}/${validPrompts.length}: Running models & scoring...`);
        const data = await pollForResults(jobId);
        results.push(data);
      }
      setAllResults(results);
    } catch (error) {
      console.error('Evaluation error:', error);
      setAllResults([{ error: error.message }]);
    } finally {
      setIsLoading(false);
      setLoadingStatus('');
    }
  };

  const handleDownloadReport = () => {
    if (!allResults) return;
    const modelNames = {};
    models.forEach(m => { modelNames[m.id] = m.name; });
    const md = generateReport(allResults, modelNames, selectedModels);
    downloadMarkdown(md, `model-evaluation-${new Date().toISOString().split('T')[0]}.md`);
  };

  const modelNames = {};
  models.forEach((m) => { modelNames[m.id] = m.name; });

  const hasResults = allResults && allResults.length > 0 && !allResults[0].error;

  return (
    <div className="eval-container">
      <div className="eval-card">
        <div className="eval-header">
          <div className="eval-header-left">
            <button onClick={onBack} className="eval-back-btn" aria-label="Back to chat">← Chat</button>
            <h1>Evaluation Mode</h1>
          </div>
          {hasResults && (
            <button onClick={handleDownloadReport} className="eval-download-btn">📄 Download Report</button>
          )}
        </div>

        <div className="eval-body">
          <div className="eval-model-picker">
            <div className="eval-model-picker-label">Select up to 3 models ({selectedModels.length}/3)</div>
            <div className="eval-model-list">
              {models.map((model) => {
                const selected = selectedModels.includes(model.id);
                const disabled = !selected && selectedModels.length >= 3;
                return (
                  <button key={model.id} className={`eval-model-chip ${selected ? 'selected' : ''} ${disabled ? 'disabled' : ''}`}
                    onClick={() => !disabled && toggleModel(model.id)} disabled={disabled}>
                    {selected && <span className="eval-model-check">✓</span>}{model.name}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="eval-prompts-section">
            <div className="eval-prompts-header">
              <span className="eval-model-picker-label">Prompts ({prompts.length})</span>
              <button type="button" onClick={addPrompt} className="eval-add-prompt-btn" disabled={prompts.length >= 10 || isLoading}>+ Add Prompt</button>
            </div>
            {prompts.map((p, idx) => (
              <div key={idx} className="eval-prompt-row">
                <input type="text" value={p} onChange={(e) => updatePrompt(idx, e.target.value)}
                  placeholder={`Prompt ${idx + 1}...`} disabled={isLoading} className="eval-input" />
                {prompts.length > 1 && (
                  <button type="button" onClick={() => removePrompt(idx)} className="eval-remove-prompt-btn" disabled={isLoading}>✕</button>
                )}
              </div>
            ))}
          </div>

          <button onClick={handleSubmit}
            disabled={isLoading || prompts.every(p => !p.trim()) || selectedModels.length === 0}
            className="eval-submit">
            {isLoading ? 'Evaluating...' : `Evaluate ${prompts.filter(p => p.trim()).length} prompt(s) × ${selectedModels.length} model(s)`}
          </button>

          {isLoading && (
            <div className="eval-loading">
              <div className="eval-spinner" />
              <span>{loadingStatus || 'Running...'}</span>
            </div>
          )}

          {hasResults && (
            <>
              <div className="eval-prompt-tabs">
                <button className={`eval-prompt-tab ${activePromptIdx === null ? 'active' : ''}`}
                  onClick={() => setActivePromptIdx(null)}>Summary</button>
                {allResults.map((r, i) => (
                  <button key={i} className={`eval-prompt-tab ${activePromptIdx === i ? 'active' : ''}`}
                    onClick={() => setActivePromptIdx(i)}>Prompt {i + 1}</button>
                ))}
              </div>

              {activePromptIdx === null ? (
                <SummaryView allResults={allResults} modelNames={modelNames} selectedModels={selectedModels} />
              ) : (
                <PromptResultView result={allResults[activePromptIdx]} modelNames={modelNames} />
              )}
            </>
          )}

          {allResults && allResults[0]?.error && (
            <div className="eval-error">Error: {allResults[0].error}</div>
          )}
        </div>
      </div>
    </div>
  );
}

function SummaryView({ allResults, modelNames, selectedModels }) {
  const n = (id) => modelNames[id] || id;
  const stats = {};
  selectedModels.forEach(id => { stats[id] = { latencies: [], costs: [], scores: [] }; });
  allResults.forEach(r => {
    (r.evaluations || []).forEach(ev => {
      const s = stats[ev.modelId];
      if (!s) return;
      if (ev.metrics) { s.latencies.push(ev.metrics.latency.totalResponseTimeMs || 0); s.costs.push(ev.metrics.cost.totalCost || 0); }
      if (ev.qualityScores?.overall) s.scores.push(ev.qualityScores.overall);
    });
  });
  const avg = (arr) => arr.length ? arr.reduce((a, b) => a + b, 0) / arr.length : 0;
  const sum = (arr) => arr.reduce((a, b) => a + b, 0);

  return (
    <div className="eval-summary">
      <div className="eval-comparison-title">Aggregate Results ({allResults.length} prompts)</div>
      <table className="eval-summary-table">
        <thead>
          <tr><th>Model</th><th>Avg Latency</th><th>Total Cost</th><th>Avg Quality</th></tr>
        </thead>
        <tbody>
          {selectedModels.map(id => (
            <tr key={id}>
              <td>{n(id)}</td>
              <td>{avg(stats[id].latencies).toFixed(0)}ms</td>
              <td>${sum(stats[id].costs).toFixed(6)}</td>
              <td>{stats[id].scores.length ? `${avg(stats[id].scores).toFixed(1)}/5` : '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function PromptResultView({ result, modelNames }) {
  if (!result || !result.evaluations) return null;
  return (
    <div>
      <div className="eval-prompt-label">Prompt: "{result.prompt}"</div>
      <div className="eval-results-grid">
        {result.evaluations.map((ev, i) => (
          <div key={i} className="eval-result-card">
            <div className="eval-result-model">{modelNames[ev.modelId] || ev.modelId}</div>
            {ev.error ? (
              <div className="eval-result-error">Error: {ev.error}</div>
            ) : (
              <>
                <div className="eval-result-response">{ev.response}</div>
                <QualityScores scores={ev.qualityScores} />
                <MetricsSummary metrics={ev.metrics} />
              </>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

export default Evaluate;
